import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import { CrownSimpleIcon, GoogleLogoIcon } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ group_name: "The King's Group of Hotels", tagline: "Heritage hospitality of the Sikkim Himalayas", logo_url: null });

  useEffect(() => {
    api.get("/settings/platform/public").then((r) => r.data && setBrand({
      group_name: r.data.group_name || "The King's Group of Hotels",
      tagline: r.data.tagline || "Heritage hospitality of the Sikkim Himalayas",
      logo_url: r.data.logo_url || null,
    })).catch(() => {});
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(`Welcome to ${brand.group_name}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirect = encodeURIComponent(window.location.origin + "/auth/callback");
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left — heritage hero */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-primary text-primary-foreground">
        {/* Layered imagery */}
        <img
          src="https://images.pexels.com/photos/36827299/pexels-photo-36827299.jpeg?auto=compress&cs=tinysrgb&h=1100&w=1600"
          alt="Sikkim Himalayan valley"
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/70 to-primary/95" />
        <div className="grain absolute inset-0" />

        {/* Mountain silhouette at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-56 mountain-silhouette" />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-14 w-full">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt="" className="h-11 w-11 rounded-full border border-gold object-contain bg-primary-foreground/10 p-1" data-testid="login-brand-logo" />
              ) : (
                <div className="h-11 w-11 rounded-full border border-gold flex items-center justify-center text-gold">
                  <CrownSimpleIcon size={22} weight="duotone" />
                </div>
              )}
              <div>
                <p className="label-overline text-primary-foreground/70">Est. Sikkim</p>
                <p className="font-display text-lg tracking-tight leading-tight">{brand.group_name}</p>
              </div>
            </div>
            <div className="prayer-flags w-40 mt-6 opacity-80" />
          </div>

          {/* Hero copy */}
          <div className="max-w-xl">
            <p className="label-overline text-primary-foreground/70 mb-4">Property Management Suite</p>
            <h1 className="font-display text-5xl xl:text-6xl leading-[1.05] tracking-tight">
              A quiet, storied way to run <em className="text-gold not-italic">heritage</em> hotels.
            </h1>
            <div className="gold-rule w-24 my-6" />
            <p className="text-primary-foreground/80 text-sm leading-relaxed max-w-md">
              {brand.tagline}. Reservations, housekeeping, billing and analytics — designed with the calm of a monastery courtyard.
            </p>
          </div>

          {/* Footer signature */}
          <div className="flex items-center justify-between text-xs text-primary-foreground/60">
            <span>© {new Date().getFullYear()} {brand.group_name}</span>
            <span className="font-mono-plex tracking-widest">GANGTOK · 1737 M ASL</span>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md p-9 border-border shadow-none">
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="" className="h-6 w-6 object-contain rounded-full border border-gold/60 p-0.5" />
            ) : (
              <CrownSimpleIcon size={20} weight="duotone" className="text-primary" />
            )}
            <span className="font-display">{brand.group_name}</span>
          </div>
          <p className="label-overline text-gold">Sign in</p>
          <h2 className="font-display text-3xl xl:text-4xl tracking-tight mt-2">Welcome back.</h2>
          <p className="text-sm text-muted-foreground mt-2">Use your staff credentials to continue.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid={AUTH.emailInput} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid={AUTH.passwordInput} value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid={AUTH.submitBtn}>
              {loading ? "Please wait…" : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <Button type="button" variant="outline" className="w-full h-11" onClick={googleLogin} data-testid={AUTH.googleBtn}>
            <GoogleLogoIcon size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>

          <p className="mt-6 text-xs text-muted-foreground text-center" data-testid="login-admin-note">
            Accounts are created by your administrator. Contact them if you don&apos;t have access yet.
          </p>
        </Card>
      </div>
    </div>
  );
}
